import { useEffect, useMemo, useRef, useState } from 'react'
import { css } from '@firebolt-dev/css'
import { HexColorPicker } from 'react-colorful'
import { cloneDeep } from 'lodash-es'
import { RAD2DEG } from 'three/src/math/MathUtils.js'
import {
  BlendIcon,
  BoneIcon,
  BoxIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  EarthIcon,
  EraserIcon,
  ExpandIcon,
  Grid2X2Icon,
  HandIcon,
  MagnetIcon,
  MapPinIcon,
  MoveIcon,
  PencilIcon,
  PipetteIcon,
  PlusIcon,
  RefreshCwIcon,
  ScanIcon,
  Trash2Icon,
  UploadIcon,
  XIcon,
} from 'lucide-react'

import { cls } from '../core/cls'
import { uuid } from '../core/utils'
import { blockLightToHex, hexToBlockLight } from '../core/chunks'

import { EditorAssetEngine } from './EditorAssetEngine'

import { UIEditorFieldNumber } from './UIEditorFieldNumber'
import { UIEditorFieldText } from './UIEditorFieldText'
import { UIEditorFieldToggle } from './UIEditorFieldToggle'
import { UIEditorFieldColor } from './UIEditorFieldColor'
import { UIEditorFieldVec3 } from './UIEditorFieldVec3'
import { UIEditorTextureWindow } from './UIEditorTextureWindow'
import { UIEditorFieldBtn } from './UIEditorFieldBtn'
import { hashFile, hashUint8Array } from '../client/hashFile'
import { deleteFile, insertFile, insertJSON } from './worldFS'
import { eulerToDegrees } from '../core/eulerToDegrees'
import { UIEditorFieldBtns } from './UIEditorFieldBtns'
import { UIEditorFieldSwitch } from './UIEditorFieldSwitch'
import { UIEditorFieldFlipper } from './UIEditorFieldFlipper'
import { orientationOptions } from '../core/BehaviorOrientation'
import { Assets } from '../core/assets'
import { produce } from 'immer'
import { useHistory } from './useHistory'
import { UIEditorFieldAsset } from './UIEditorFieldAsset'

const NodeTypeNames = {
  bone: 'Bone',
  box: 'Box',
  hitbox: 'Hitbox',
  collider: 'Collider',
}

const randomOffsetAxisLabels = ['W-E', 'U-D', 'N-S']
const cardinalLabels = ['N', 'E', 'S', 'W', 'U', 'D']
const connectionLabels = ['Same', 'Solid']
const windOptions = [
  { label: 'None', value: null },
  { label: 'Anchor', value: 'anchor' },
  { label: 'Free', value: 'free' },
]

export function UIEditorAssetPane({ client, tab, editTab, visible, showModal }) {
  const [data, setData] = useState(null)

  console.log('tab', tab)

  useEffect(() => {
    const load = async () => {
      const registry = client.registry

      const asset = cloneDeep(tab.data.asset)

      editTab(t => {
        t.icon = asset.type
        t.label = asset.id
      })

      let texture = cloneDeep(tab.data.texture)
      if (!texture) {
        texture = {
          id: uuid(),
          type: 'texture',
          width: 32,
          height: 32,
          data: null,
        }
        asset.model.texture = texture.id
      }
      const canvas = document.createElement('canvas')
      canvas.width = texture.width
      canvas.height = texture.height
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (texture.url) {
        const resp = await fetch(texture.url)
        const blob = await resp.blob()
        const bitmap = await createImageBitmap(blob)
        ctx.drawImage(bitmap, 0, 0)
      } else {
        ctx.fillStyle = 'black'
        ctx.fillRect(0, 0, texture.width, texture.height)
      }
      texture.canvas = canvas
      texture.ctx = ctx
      texture.imageData = ctx.getImageData(0, 0, texture.width, texture.height)
      texture.version = 0

      const icon = cloneDeep(tab.data.icon)

      const ui = {
        mode: 'build',
        gizmo: 'move',
        node: null,
        nodeId: null,
        snap: true,
        space: 'world',
        grid: true,
        paintMode: 'face',
        colorPicker: false,
        color: '#ff0000',
        pose: 'icon',
        version: 0,
      }

      setData({
        asset,
        texture,
        icon,
        ui,
      })
    }
    load()
  }, [])

  if (!data) return null
  return <Main client={client} data={data} tab={tab} editTab={editTab} visible={visible} showModal={showModal} />
}

function Main({ client, data: initialData, tab, editTab, visible, showModal }) {
  const initRef = useRef()
  const history = useHistory(initialData)
  const engine = useMemo(() => new EditorAssetEngine(), [])
  const viewportRef = useRef()
  const [colorPicker, setColorPicker] = useState(false)
  const [dirty, setDirty] = useState(false)

  const { asset, texture, ui } = history.data

  const node = ui.nodeId ? findNode(asset.model.root.children, ui.nodeId) : null

  function markDirty() {
    // ignore first
    if (!initRef.current) {
      initRef.current = true
      return
    }
    if (dirty) return
    const save = async () => {
      const ops = { remove: [], upsert: [] }
      const registry = client.registry
      const isNew = tab.data.isNew

      // =======
      // Texture
      // =======

      const texture = {}
      const textureData = await canvasToUint8Array(history.data.texture.canvas)
      texture.id = await hashUint8Array(textureData)
      texture.type = 'texture'
      texture.width = history.data.texture.canvas.width
      texture.height = history.data.texture.canvas.height
      texture.data = textureData

      ops.upsert.push(texture)

      // ====
      // Icon
      // ====

      // const icon = await engine.captureIcon(`${asset.id}.png`)
      const icon = {}
      const iconInfo = await engine.captureIcon()
      icon.id = await hashUint8Array(iconInfo.data)
      icon.type = 'icon'
      icon.width = iconInfo.width
      icon.height = iconInfo.height
      icon.data = iconInfo.data

      ops.upsert.push(icon)

      // =====
      // Asset
      // =====

      const asset = cloneDeep(history.data.asset)
      asset.icon = icon.id
      asset.model.texture = texture.id

      // if this is a new asset OR the asset changed its id, ensure the id is available
      if (isNew || (!isNew && tab.data.asset.id !== asset.id)) {
        const taken = !!registry.raw.find(a => a.type === asset.type && a.id === asset.id)
        if (taken) {
          await showModal({
            title: 'Duplicate ID',
            message: `An asset with the ID '${asset.id}' already exists.`,
            btns: [
              {
                label: 'Okay',
                action: null,
              },
            ],
          })
          return false
        }
      }
      // if updating and id changed, delete old asset
      if (!isNew && tab.data.asset.id !== asset.id) {
        ops.remove.push({ type: tab.data.asset.type, id: tab.data.asset.id })
        // delete rootStore.assets[_tab.asset.id]
        // await deleteFile(rootStore.dir, `/${_tab.asset.type}s/${_tab.asset.id}.json`)
      }
      ops.upsert.push(asset)

      client.network.send('editAssets', ops)

      console.log('save', isNew, asset, texture, icon)

      editTab(t => {
        t.data.asset = asset
        t.data.texture = texture
        t.data.icon = icon
        t.data.isNew = false
        t.save = null
        console.log('editTab', t)
      })
      setDirty(false)
    }
    editTab(t => {
      t.save = save
    })
    setDirty(true)
  }

  function setLabel(value) {
    editTab(t => (t.label = value))
  }

  useEffect(() => {
    const viewport = viewportRef.current

    engine.init({ viewport })

    engine.on('selected', obj => {
      history.edit(data => {
        // store.asset.ui.node = findNode(store.asset.model.root.children, nodeId)
        data.ui.nodeId = obj?._nodeId || null
      })
      history.commit()
    })
    engine.on('transform', e => {
      history.edit(data => {
        let target
        // build transforms
        if (data.ui.mode === 'build' || data.ui.mode === 'paint') {
          target = findNode(data.asset.model.root.children, data.ui.nodeId)
        }
        // pose transforms
        else if (data.ui.mode === 'pose') {
          target = data.asset.pose[data.ui.pose]
        }
        if (!target) {
          return console.warn('on transform but no target')
        }
        // update
        if (target.position) {
          target.position = e.obj.position.toArray()
        }
        if (target.rotation) {
          // target.rotation = e.obj.rotation.toArray()
          // target.rotation = target.rotation.map(n => n * RAD2DEG)
          eulerToDegrees(e.obj.rotation, target.rotation)
        }
        if (target.scale) {
          target.scale = e.obj.scale.toArray()
        }
      })
      // no commit!
    })
    engine.on('transformEnd', () => {
      history.edit(data => {
        data.ui.version++
      })
      history.commit()
    })
  }, [])

  useEffect(() => {
    if (!visible) return
    function onKeyDown(e) {
      const isCtrl = e.metaKey || e.ctrlKey
      const isShift = e.shiftKey
      const key = e.key.toLowerCase()
      if (key === 'z' && isCtrl && !isShift) history.undo()
      if (key === 'z' && isCtrl && isShift) history.redo()
      if (key === 'y' && isCtrl && !isShift) history.redo()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [visible])

  useEffect(() => {
    // const texture = asset.model.texture
    // console.log('tv', texture.version)
    // if (texture.version === 0) return // ignore first
    texture.ctx.putImageData(texture.imageData, 0, 0)
  }, [texture.version])

  useEffect(() => setLabel(asset.id), [asset.id])
  useEffect(() => markDirty(), [ui.version, texture.version])
  useEffect(() => engine.setMode(ui.mode), [ui.mode])
  useEffect(() => engine.setSnap(ui.snap), [ui.snap])
  useEffect(() => engine.setGrid(ui.grid), [ui.grid])
  useEffect(() => engine.setSpace(ui.space), [ui.space])
  useEffect(() => engine.setGizmo(ui.gizmo), [ui.gizmo])
  useEffect(() => engine.setSelected(ui.nodeId), [ui.nodeId])
  useEffect(() => engine.setPose(ui.pose), [ui.pose])
  useEffect(() => engine.setAsset(asset, texture, ui), [ui.version, texture.version, ui.mode, ui.pose])
  useEffect(() => engine.setActive(visible), [visible])

  function add(type) {
    history.edit(data => {
      const id = getNewId(type, asset.model.root)
      const node = createNode(id, type)
      data.asset.model.root.children.push(node)
      // data.ui.node = node
      data.ui.nodeId = node?.id
      data.ui.version++
    })
    history.commit()
  }

  function setMode(value) {
    history.edit(data => {
      data.ui.mode = value
    })
    history.commit()
  }

  function setGizmo(value) {
    history.edit(data => {
      data.ui.gizmo = value
    })
    history.commit()
  }

  function toggleSnap() {
    history.edit(data => {
      data.ui.snap = !data.ui.snap
    })
    history.commit()
  }

  function setSpace(value) {
    history.edit(data => {
      data.ui.space = value
    })
    history.commit()
  }

  function toggleGrid() {
    history.edit(data => {
      data.ui.grid = !data.ui.grid
    })
    history.commit()
  }

  function setPaintMode(value) {
    history.edit(data => {
      data.ui.paintMode = value
    })
    history.commit()
  }

  return (
    <div
      className='sasset'
      css={css`
        flex: 1;
        display: flex;
        align-items: stretch;
        /* background: var(--bg1); */
        .sasset-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          margin-right: 10px;
        }
        .sasset-main-top {
          flex: 1;
          position: relative;
          background: var(--bg2);
          border-radius: 5px;
        }
        .sasset-main-btm {
          flex: 1;
          background: var(--bg2);
          border-radius: 5px;
          position: relative;
          margin-top: 10px;
        }
        .sasset-viewport {
          position: absolute;
          inset: 0;
        }
        .sasset-sidebar {
          width: 300px;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          gap: 10px;
        }
        .sasset-sidebar-pane {
          background: var(--bg3);
          border-radius: 5px;
          padding: 15px;
          overflow-y: auto;
          &.nodes {
            height: 30%;
          }
          &.general,
          &.nodeprops {
            height: 70%;
          }
          &.pose {
            height: 100%;
          }
        }
        /* .sasset-panel {
          width: 300px;
          background: var(--bg3);
          border-radius: 5px;
          display: flex;
          flex-direction: column;
          align-items: stretch;
        } */
        /* .sasset-nodes {
          padding: 15px;
          height: 30%;
          overflow-y: auto;
        } */
        .sasset-title {
          display: flex;
          align-items: center;
          &-text {
            flex: 1;
            font-size: 20px;
            font-weight: 600;
          }
          &-btn {
            cursor: pointer;
          }
        }
        .sasset-fieldlabel {
          display: flex;
          align-items: center;
          margin: 20px 0 8px;
          &:first-child {
            margin-top: 0;
          }
          span {
            flex: 1;
            color: rgba(255, 255, 255, 0.6);
          }
          &-btn {
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            &:hover {
              cursor: pointer;
              color: white;
            }
          }
        }
        .sasset-fieldsub {
          margin-top: 4px;
        }
        /* .sasset-general {
          border-top: 1px solid var(--bg4);
          padding: 15px;
          height: 70%;
          overflow-y: auto;
        } */
        /* .sasset-properties {
          border-top: 1px solid var(--bg4);
          padding: 15px;
          height: 70%;
          overflow-y: auto;
        } */
        .sasset-modebar {
          position: absolute;
          top: 10px;
          left: 10px;
          height: 44px;
          background: var(--bg3);
          border-radius: 5px;
          padding: 0 10px;
          display: flex;
          align-items: center;
          &-mode {
            cursor: pointer;
            padding: 0 6px;
            height: 40px;
            color: #64647e;
            display: flex;
            align-items: center;
            justify-content: center;
            &.selected {
              color: white;
            }
          }
          &-line {
            height: 44px;
            border-right: 1px solid var(--bg4);
            margin: 0 10px;
          }
          &-btn {
            width: 34px;
            height: 34px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            &.green {
              color: #33ba33;
            }
            &.red {
              color: #e13d3d;
            }
          }
        }
        .sasset-toolbar {
          position: absolute;
          top: 10px;
          right: 10px;
          height: 44px;
          background: var(--bg3);
          border-radius: 5px;
          display: flex;
          align-items: center;
        }
        .sasset-toolbar-group {
          display: flex;
          align-items: center;
          padding: 0 4px;
          border-right: 1px solid var(--bg4);
          &:last-child {
            border-right: 0;
          }
        }
        .sasset-toolbar-btn {
          height: 40px;
          width: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3c3c4c;
          &:hover {
            cursor: pointer;
          }
          &:hover:not(.selected):not(.active) {
            color: #54546b;
          }
          &.selected {
            color: white;
          }
          &.active {
            color: white;
          }
        }
        .sasset-toolbar-color {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          &-preview {
            width: 22px;
            height: 22px;
            border-radius: 4px;
          }
          &-picker {
            position: absolute;
            top: -10px;
            left: 50%;
            background: red;
            border-radius: 10px;
            transform: translate(-50%, -100%);
            z-index: 2;
          }
        }
        .sasset-texture {
          position: absolute;
          right: 20px;
          bottom: 80px;
          background: #191920;
          border-radius: 10px;
          width: 500px;
          height: 700px;
        }
        .sasset-nodetypes {
          display: flex;
          align-items: center;
          gap: 5px;
          margin: 0 0 20px;
        }
        .sasset-nodetype {
          flex: 1;
          height: 36px;
          background: var(--bg4);
          border-radius: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
          &:hover {
            cursor: pointer;
            background: var(--bg5);
          }
        }

        // nodes hierarchy
        .sasset-nodegroup {
          position: relative;
        }
        .sasset-node {
          position: relative;
          border-radius: 5px;
          &.selected {
            background-color: var(--bg5);
          }
          &:hover:not(.selected) {
            background-color: var(--bg4);
          }
          &:hover {
            cursor: pointer;
          }
          &.dragging {
            opacity: 0.5;
            .sasset-nodegroup {
              display: none; // hide children
            }
            /* transform: scale(0.5); */
          }
        }
        .sasset-node-dropzone {
          position: absolute;
          height: 4px;
          right: 0;
          &.before {
            z-index: 5;
            top: 0px;
            &.highlight {
              border-top: 2px solid gold;
            }
          }
          &.after {
            z-index: 6;
            bottom: 0px;
            &.highlight {
              border-bottom: 2px solid gold;
            }
          }
          &.child {
            bottom: 0px;
            &.highlight {
              border-bottom: 2px solid gold;
            }
          }
        }
        .sasset-node-inner {
          height: 36px;
          display: flex;
          align-items: center;
          padding: 0 10px 0 12px;
        }
        .sasset-node-icon {
          margin-right: 10px;
          flex-shrink: 0;
        }
        .sasset-node-name {
          flex: 1;
          &.input {
            // todo: selection and text color?
          }
        }
        .sasset-node-btn {
          width: 22px;
          height: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(126, 126, 126, 1);
          &:hover {
            cursor: pointer;
            color: white;
          }
        }
      `}
    >
      <div className='sasset-main'>
        <div className='sasset-main-top'>
          <div className='sasset-viewport' ref={viewportRef} />
          <div className='sasset-modebar'>
            <div
              className={cls('sasset-modebar-mode', { selected: ui.mode === 'build' })}
              onClick={() => setMode('build')}
            >
              <span>Build</span>
            </div>
            <div
              className={cls('sasset-modebar-mode', { selected: ui.mode === 'paint' })}
              onClick={() => setMode('paint')}
            >
              <span>Paint</span>
            </div>
            <div
              className={cls('sasset-modebar-mode', { selected: ui.mode === 'pose' })}
              onClick={() => setMode('pose')}
            >
              <span>Pose</span>
            </div>
            {/* <div className='sasset-modebar-line' /> */}
          </div>
          {ui.mode === 'build' && (
            <div className='sasset-toolbar'>
              {node && (
                <div className='sasset-toolbar-group'>
                  <div
                    className={cls('sasset-toolbar-btn', { selected: ui.gizmo === 'move' })}
                    onClick={() => setGizmo('move')}
                  >
                    <MoveIcon size={20} />
                  </div>
                  {node.rotation && (
                    <div
                      className={cls('sasset-toolbar-btn', { selected: ui.gizmo === 'rotate' })}
                      onClick={() => setGizmo('rotate')}
                    >
                      <RefreshCwIcon size={20} />
                    </div>
                  )}
                  {node.scale && (
                    <div
                      className={cls('sasset-toolbar-btn', { selected: ui.gizmo === 'scale' })}
                      onClick={() => setGizmo('scale')}
                    >
                      <ExpandIcon size={20} />
                    </div>
                  )}
                </div>
              )}
              <div className='sasset-toolbar-group'>
                <div className={cls('sasset-toolbar-btn', { selected: ui.snap })} onClick={() => toggleSnap()}>
                  <MagnetIcon size={20} />
                </div>
              </div>
              <div className='sasset-toolbar-group'>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.space === 'local' })}
                  onClick={() => setSpace('local')}
                >
                  <MapPinIcon size={20} />
                </div>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.space === 'world' })}
                  onClick={() => setSpace('world')}
                >
                  <EarthIcon size={20} />
                </div>
              </div>
              <div className='sasset-toolbar-group'>
                <div className={cls('sasset-toolbar-btn', { selected: ui.grid })} onClick={() => toggleGrid()}>
                  <Grid2X2Icon size={20} />
                </div>
              </div>
            </div>
          )}

          {ui.mode === 'pose' && (
            <div className='sasset-toolbar'>
              <div className='sasset-toolbar-group'>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.gizmo === 'move' })}
                  onClick={() => setGizmo('move')}
                >
                  <MoveIcon size={20} />
                </div>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.gizmo === 'rotate' })}
                  onClick={() => setGizmo('rotate')}
                >
                  <RefreshCwIcon size={20} />
                </div>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.gizmo === 'scale' })}
                  onClick={() => setGizmo('scale')}
                >
                  <ExpandIcon size={20} />
                </div>
              </div>
              <div className='sasset-toolbar-group'>
                <div className={cls('sasset-toolbar-btn', { selected: ui.snap })} onClick={() => toggleSnap()}>
                  <MagnetIcon size={20} />
                </div>
              </div>
              {/* <div className='sasset-toolbar-group'>
            <div
              className={cls('sasset-toolbar-btn', { selected: ui.space === 'local' })}
              onClick={() => setSpace('local')}
            >
              <MapPinIcon size={20} />
            </div>
            <div
              className={cls('sasset-toolbar-btn', { selected: ui.space === 'world' })}
              onClick={() => setSpace('world')}
            >
              <EarthIcon size={20} />
            </div>
          </div> */}
            </div>
          )}
        </div>
        {ui.mode === 'paint' && (
          <div className='sasset-main-btm'>
            <UIEditorTextureWindow history={history} />
            <div className='sasset-toolbar'>
              {ui.paintMode === 'draw' && (
                <div className='sasset-toolbar-group'>
                  <div
                    tabIndex={0}
                    className='sasset-toolbar-color'
                    onFocus={() => setColorPicker(ui.color)}
                    onBlur={() => {
                      setColorPicker(false)
                    }}
                    onPointerUp={() => {
                      if (colorPicker !== ui.color) {
                        history.commit()
                      }
                    }}
                  >
                    <Unmount onUnmount={() => setColorPicker(false)} />
                    <div
                      className='sasset-toolbar-color-preview'
                      css={css`
                        background-color: ${ui.color};
                      `}
                    />
                    {colorPicker && (
                      <div className='sasset-toolbar-color-picker'>
                        <HexColorPicker
                          color={ui.color}
                          onChange={color => {
                            history.edit(data => {
                              data.ui.color = color
                              // no commit (handled in parent above)
                            })
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div
                    className='sasset-toolbar-btn active'
                    // onClick={() => (store.asset.ui.paintMode = 'picker')}
                  >
                    <PipetteIcon size={20} />
                  </div>
                </div>
              )}
              <div className='sasset-toolbar-group'>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.paintMode === 'face' })}
                  onClick={() => setPaintMode('face')}
                >
                  <HandIcon size={20} />
                </div>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.paintMode === 'draw' })}
                  onClick={() => setPaintMode('draw')}
                >
                  <PencilIcon size={20} />
                </div>
                <div
                  className={cls('sasset-toolbar-btn', { selected: ui.paintMode === 'erase' })}
                  onClick={() => setPaintMode('erase')}
                >
                  <EraserIcon size={20} />
                </div>
              </div>
              <div className='sasset-toolbar-group'>
                <div
                  className='sasset-toolbar-btn active'
                  onClick={() => {
                    downloadTexture(texture, `${texture.id}.png`)
                  }}
                >
                  <DownloadIcon size={20} />
                </div>
                <label className='sasset-toolbar-btn active'>
                  <UploadIcon size={18} />
                  <ImportInput
                    onBitmap={bitmap => {
                      history.edit(data => {
                        const texture = data.texture
                        texture.canvas.width = bitmap.width
                        texture.canvas.height = bitmap.height
                        texture.ctx.drawImage(bitmap, 0, 0)
                        const imageData = texture.ctx.getImageData(0, 0, bitmap.width, bitmap.height)
                        texture.width = bitmap.width
                        texture.height = bitmap.height
                        texture.filename = null
                        texture.imageData = imageData
                        texture.version++
                        data.ui.version++
                      })
                      history.commit()
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className='sasset-sidebar'>
        {(ui.mode === 'build' || ui.mode === 'paint') && (
          <div className='sasset-sidebar-pane nodes noscrollbar'>
            <div className='sasset-nodetypes'>
              <div className='sasset-nodetype' onClick={() => add('bone')}>
                <BoneIcon size={20} />
              </div>
              <div className='sasset-nodetype' onClick={() => add('box')}>
                <BoxIcon size={20} />
              </div>
              <div className='sasset-nodetype' onClick={() => add('hitbox')}>
                <ScanIcon size={20} />
              </div>
              <div className='sasset-nodetype' onClick={() => add('collider')}>
                <BlendIcon size={20} />
              </div>
            </div>

            <Nodes
              nodes={asset.model.root.children}
              depth={0}
              selectedNodeId={node?.id}
              setSelectedNodeId={nodeId => {
                if (ui.nodeId === nodeId) return
                // store.asset.ui.node = findNode(store.asset.model.root.children, nodeId)[0]
                history.edit(data => {
                  data.ui.nodeId = nodeId // store.asset.ui.nodeId
                })
                history.commit()
              }}
              edit={fn => {
                history.edit(fn)
                history.commit()
              }}
              draggedNode={ui.draggedNode}
              setDraggedNode={value => {
                history.edit(data => {
                  data.ui.draggedNode = value
                })
              }}
              dropTarget={ui.dropTarget}
              setDropTarget={value => {
                history.edit(data => {
                  data.ui.dropTarget = value
                })
              }}
              onDrop={() => {
                history.edit(data => {
                  if (!data.ui.draggedNode || !data.ui.dropTarget) return
                  // ensure we're not nesting inside ourselves
                  const [dragNode, dragParent, dragIdx] = findNodeInfo(
                    data.asset.model.root.children,
                    data.ui.draggedNode.id
                  )
                  const isNested = findNode(dragNode.children, data.ui.dropTarget.id)
                  if (isNested) {
                    data.ui.draggedNode = null
                    data.ui.dropTarget = null
                    return
                  }
                  // remove from old location
                  if (dragParent) {
                    dragParent.children.splice(dragIdx, 1)
                  } else {
                    data.asset.model.root.children.splice(dragIdx, 1)
                  }
                  // add to new location
                  if (data.ui.dropTarget.position === 'child') {
                    const dropNode = findNode(data.asset.model.root.children, data.ui.dropTarget.id)
                    dropNode.children.unshift(dragNode)
                  } else {
                    const [dropNode, dropParent, dropIdx] = findNodeInfo(
                      data.asset.model.root.children,
                      data.ui.dropTarget.id
                    )
                    const insertIdx = data.ui.dropTarget.position === 'before' ? dropIdx : dropIdx + 1
                    if (dropParent) {
                      dropParent.children.splice(insertIdx, 0, dragNode)
                    } else {
                      data.asset.model.root.children.splice(insertIdx, 0, dragNode)
                    }
                  }
                  data.ui.draggedNode = null
                  data.ui.dropTarget = null
                  data.ui.version++
                })
                history.commit()
              }}
            />
          </div>
        )}
        {(ui.mode === 'build' || ui.mode === 'paint') && !node && (
          <div className='sasset-sidebar-pane general noscrollbar'>
            <div className='sasset-fieldlabel'>ID</div>
            <UIEditorFieldText
              value={asset.id}
              onChange={value => {
                history.edit(data => {
                  data.asset.id = value
                  data.ui.version++
                })
                history.commit()
              }}
            />
            {asset.type === 'block' && (
              <>
                <div className='sasset-fieldlabel'>Solid Sides</div>
                <UIEditorFieldBtns
                  value={asset.solid}
                  labels={cardinalLabels}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.solid = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Opaque Sides</div>
                <UIEditorFieldBtns
                  value={asset.opaque}
                  labels={cardinalLabels}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.opaque = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Attenuation</div>
                <UIEditorFieldNumber
                  value={asset.attenuation}
                  min={1}
                  max={15}
                  dp={1}
                  step={1}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.attenuation = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Replaceable</div>
                <UIEditorFieldToggle
                  value={asset.replaceable}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.replaceable = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Light Emission</div>
                <UIEditorFieldColor
                  value={blockLightToHex(asset.light)}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.light = hexToBlockLight(value)
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Face Dimming</div>
                <UIEditorFieldToggle
                  trueLabel='On'
                  falseLabel='Off'
                  value={asset.dimming}
                  onChange={value => {
                    console.log('face dimming 1')
                    history.edit(data => {
                      console.log('face dimming 2')
                      // console.log('face dimming 3', data)
                      data.asset.dimming = value
                      data.ui.version++
                    })
                    console.log('face dimming 4')
                    history.commit()
                    console.log('face dimming 5')
                  }}
                />
                <div className='sasset-fieldlabel'>Orientation</div>
                <UIEditorFieldFlipper
                  options={orientationOptions}
                  value={asset.behaviors.orientation}
                  onChange={value => {
                    history.edit(data => {
                      if (value) {
                        data.asset.behaviors.orientation = value
                      } else {
                        delete data.asset.behaviors.orientation
                      }
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Touch Sound</div>
                <UIEditorFieldAsset
                  registry={client.registry}
                  type='sound'
                  value={asset.touchSound}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.touchSound = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Place Sound</div>
                <UIEditorFieldAsset
                  registry={client.registry}
                  type='sound'
                  value={asset.placeSound}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.placeSound = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Break Sound</div>
                <UIEditorFieldAsset
                  registry={client.registry}
                  type='sound'
                  value={asset.breakSound}
                  onChange={value => {
                    history.edit(data => {
                      data.asset.breakSound = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                <div className='sasset-fieldlabel'>Random Offset</div>
                <UIEditorFieldToggle
                  value={asset.behaviors.randomOffset}
                  onChange={value => {
                    history.edit(data => {
                      if (value) {
                        data.asset.behaviors.randomOffset = { axis: [true, false, true], max: 8 }
                      } else {
                        delete data.asset.behaviors.randomOffset
                      }
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                {asset.behaviors.randomOffset && (
                  <>
                    <div className='sasset-fieldsub'>
                      <UIEditorFieldBtns
                        value={asset.behaviors.randomOffset.axis}
                        labels={randomOffsetAxisLabels}
                        onChange={value => {
                          history.edit(data => {
                            data.asset.behaviors.randomOffset.axis = value
                            data.ui.version++
                          })
                          history.commit()
                        }}
                      />
                    </div>
                    <div className='sasset-fieldsub'>
                      <UIEditorFieldNumber
                        value={asset.behaviors.randomOffset.max}
                        min={0}
                        max={16}
                        dp={1}
                        step={1}
                        onChange={value => {
                          history.edit(data => {
                            data.asset.behaviors.randomOffset.max = value
                            data.ui.version++
                          })
                          history.commit()
                        }}
                      />
                    </div>
                  </>
                )}
                <div className='sasset-fieldlabel'>Connection Props</div>
                <UIEditorFieldToggle
                  value={asset.behaviors.connections}
                  onChange={value => {
                    history.edit(data => {
                      if (value) {
                        data.asset.behaviors.connections = { same: true, solid: true }
                      } else {
                        delete data.asset.behaviors.connections
                      }
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
                {asset.behaviors.connections && (
                  <div className='sasset-fieldsub'>
                    <UIEditorFieldBtns
                      value={[asset.behaviors.connections.same, asset.behaviors.connections.solid]}
                      labels={connectionLabels}
                      onChange={value => {
                        history.edit(data => {
                          data.asset.behaviors.connections.same = value[0]
                          data.asset.behaviors.connections.solid = value[1]
                          data.ui.version++
                        })
                        history.commit()
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {(ui.mode === 'build' || ui.mode === 'paint') && node && (
          <div className='sasset-sidebar-pane nodeprops noscrollbar'>
            <div className='sasset-title'>
              <div className='sasset-title-text'>{NodeTypeNames[node.type]}</div>
              <div
                className='sasset-title-btn'
                onClick={() => {
                  history.edit(data => {
                    data.ui.nodeId = null
                  })
                  history.commit()
                }}
              >
                <XIcon size={20} />
              </div>
            </div>
            <div className='sasset-fieldlabel'>ID</div>
            <UIEditorFieldText
              value={node.id}
              onChange={id => {
                history.edit(data => {
                  const match = findNode(data.asset.model.root.children, node.id)
                  match.id = getNewId(id, data.asset.model.root, node.id)
                  data.ui.nodeId = match.id
                  data.ui.version++
                })
                history.commit()
              }}
            />
            {node.size && (
              <>
                <div className='sasset-fieldlabel'>Size</div>
                <UIEditorFieldVec3
                  value={node.size}
                  onChange={value => {
                    history.edit(data => {
                      findNode(data.asset.model.root.children, node.id).size = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                  min={0}
                />
              </>
            )}
            {node.position && (
              <>
                <div className='sasset-fieldlabel'>Position</div>
                <UIEditorFieldVec3
                  value={node.position}
                  onChange={value => {
                    history.edit(data => {
                      findNode(data.asset.model.root.children, node.id).position = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                  dp={2}
                />
              </>
            )}
            {node.rotation && (
              <>
                <div className='sasset-fieldlabel'>Rotation</div>
                <UIEditorFieldVec3
                  value={node.rotation}
                  onChange={value => {
                    history.edit(data => {
                      findNode(data.asset.model.root.children, node.id).rotation = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                  dp={2}
                />
              </>
            )}
            {node.scale && (
              <>
                <div className='sasset-fieldlabel'>Scale</div>
                <UIEditorFieldVec3
                  value={node.scale}
                  onChange={value => {
                    history.edit(data => {
                      findNode(data.asset.model.root.children, node.id).scale = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                  dp={2}
                />
              </>
            )}
            {node.type === 'box' && (
              <>
                <div className='sasset-fieldlabel'>Wind</div>
                <UIEditorFieldSwitch
                  options={windOptions}
                  value={node.wind || null}
                  onChange={value => {
                    history.edit(data => {
                      findNode(data.asset.model.root.children, node.id).wind = value
                      data.ui.version++
                    })
                    history.commit()
                  }}
                />
              </>
            )}
            <div className='sasset-fieldlabel'>
              <span>Conditions</span>
              <div
                className='sasset-fieldlabel-btn'
                onClick={() => {
                  history.edit(data => {
                    const node_ = findNode(data.asset.model.root.children, node.id)
                    node_.conditions.push({ prop: '', op: 'eq', value: '' })
                    data.ui.version++
                  })
                  history.commit()
                }}
              >
                <PlusIcon size={18} />
              </div>
            </div>
            {node.conditions.map((condition, idx) => (
              <ConditionField
                key={idx}
                condition={condition}
                onChange={(key, value) => {
                  history.edit(data => {
                    const node_ = findNode(data.asset.model.root.children, node.id)
                    node_.conditions[idx][key] = value
                    data.ui.version++
                  })
                  history.commit()
                }}
                onRemove={() => {
                  history.edit(data => {
                    const node_ = findNode(data.asset.model.root.children, node.id)
                    node_.conditions.splice(idx, 1)
                    data.ui.version++
                  })
                  history.commit()
                }}
              />
            ))}
          </div>
        )}
        {ui.mode === 'pose' && (
          <div className='sasset-sidebar-pane pose noscrollbar'>
            <div className='sasset-pose-types'>
              <div
                className={cls('sasset-pose-type', { active: ui.pose === 'icon' })}
                onClick={() => {
                  history.edit(data => {
                    data.ui.pose = 'icon'
                  })
                  history.commit()
                }}
              >
                <span>Icon</span>
              </div>
              <div
                className={cls('sasset-pose-type', { active: ui.pose === 'socket' })}
                onClick={() => {
                  history.edit(data => {
                    data.ui.pose = 'socket'
                  })
                  history.commit()
                }}
              >
                <span>Socket</span>
              </div>
              <div
                className={cls('sasset-pose-type', { active: ui.pose === 'hand' })}
                onClick={() => {
                  history.edit(data => {
                    data.ui.pose = 'hand'
                  })
                  history.commit()
                }}
              >
                <span>Hand</span>
              </div>
              <div
                className={cls('sasset-pose-type', { active: ui.pose === 'ground' })}
                onClick={() => {
                  history.edit(data => {
                    data.ui.pose = 'ground'
                  })
                  history.commit()
                }}
              >
                <span>Ground</span>
              </div>
            </div>
            <div className='sasset-fieldlabel'>Position</div>
            <UIEditorFieldVec3
              value={asset.pose[ui.pose].position}
              onChange={value => {
                history.edit(data => {
                  data.asset.pose[data.ui.pose].position = value
                  data.ui.version++
                })
                history.commit()
              }}
              dp={2}
            />
            <div className='sasset-fieldlabel'>Rotation</div>
            <UIEditorFieldVec3
              value={asset.pose[ui.pose].rotation}
              onChange={value => {
                history.edit(data => {
                  data.asset.pose[data.ui.pose].rotation = value
                  data.ui.version++
                })
                history.commit()
              }}
              dp={2}
            />
            <div className='sasset-fieldlabel'>Scale</div>
            <UIEditorFieldVec3
              value={asset.pose[ui.pose].scale}
              onChange={value => {
                history.edit(data => {
                  data.asset.pose[data.ui.pose].scale = value
                  data.ui.version++
                })
                history.commit()
              }}
              dp={2}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function downloadTexture(texture, filename = 'texture.png') {
  texture.canvas.toBlob(blob => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, 'image/png')
}

function ImportInput({ onBitmap }) {
  const [n, setN] = useState(0)
  return (
    <input
      key={n}
      css={css`
        position: absolute;
        top: -9999px;
        left: -9999px;
        opacity: 0;
        overflow: hidden;
      `}
      type='file'
      accept='image/jpeg,image/png,image/webp'
      onChange={async e => {
        try {
          const file = e.target.files && e.target.files[0]
          if (!file) return
          // Basic type guard (MIME first, filename fallback)
          const allowedMimes = new Set(['image/jpeg', 'image/png', 'image/webp'])
          const name = (file.name || '').toLowerCase()
          const ext = name.split('.').pop()
          const allowedExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext)
          if (!allowedMimes.has(file.type) && !allowedExt) {
            console.warn('Unsupported file type:', file.type || name)
            // bail out quietly; or throw if you want a UI error
            setN(x => x + 1)
            return
          }
          // Decode -> draw to canvas -> read back RGBA
          const bitmap = await createImageBitmap(file) // modern, handles most formats incl. webp
          onBitmap(bitmap)
          // return
          // if (bitmap.width > 256 || bitmap.height > 256) {
          //   console.error('imported texture size must be less than 256 x 256')
          //   setN(x => x + 1)
          //   return
          // }
          // const canvas = document.createElement('canvas')
          // canvas.width = bitmap.width
          // canvas.height = bitmap.height
          // const ctx = canvas.getContext('2d', { willReadFrequently: true })
          // if (!ctx) {
          //   throw new Error('Could not acquire 2D context')
          // }
          // ctx.drawImage(bitmap, 0, 0)
          // const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          // // ImageData.data is Uint8ClampedArray; convert to Array for your API
          // // const pixels = Array.from(imageData.data) // copies bytes into an un-clamped buffer
          // // Close bitmap to free memory ASAP
          // // if (typeof bitmap.close === 'function') bitmap.close()
          // onImageData(imageData)
        } catch (err) {
          console.error('Import failed:', err)
        } finally {
          // reset input so the same file can be chosen again
          setN(x => x + 1)
        }
      }}
    />
  )
}

function getNewId(id, root, prevId) {
  const exists = testId => testId !== prevId && !!findNode(root.children, testId)

  if (!exists(id)) return id

  const m = id.match(/^(.*?)(?:\.(\d+))?$/)
  let base = m[1]
  let num = m[2] ? Number(m[2]) : 0

  if (!m[2]) num = 1

  let candidate
  do {
    candidate = `${base}.${String(num++).padStart(2, '0')}`
  } while (exists(candidate))

  return candidate
}

function createNode(id, type) {
  const node = {
    id,
    type,
    conditions: [],
    children: [],
  }
  if (type === 'group') {
    // todo: ui button!
    node.position = [0, 0, 0]
    node.rotation = [0, 0, 0]
    node.scale = [1, 1, 1]
  }
  if (type === 'bone') {
    node.position = [0, 0, 0]
    node.rotation = [0, 0, 0]
    node.scale = [1, 1, 1]
  }
  if (type === 'box') {
    node.position = [0, 16, 0]
    node.rotation = [0, 0, 0]
    node.scale = [1, 1, 1]
    node.size = [32, 32, 32]
    node.wind = null
    // prettier-ignore
    node.faces = {
      up: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
      down: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
      north: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
      east: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
      south: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
      west: { x:0, y:0, rotation:0, density:1, flipX:false, flipY:false }, 
    }
  }
  if (type === 'hitbox') {
    node.position = [0, 16, 0]
    node.size = [32, 32, 32]
  }
  if (type === 'collider') {
    node.position = [0, 16, 0]
    node.rotation = [0, 0, 0]
    node.scale = [1, 1, 1]
    node.size = [32, 32, 32]
  }
  return node
}

function Unmount({ onUnmount }) {
  useEffect(() => {
    return () => onUnmount?.()
  }, [])
}

function Nodes({
  nodes,
  depth,
  selectedNodeId,
  setSelectedNodeId,
  edit,
  draggedNode,
  setDraggedNode,
  dropTarget,
  setDropTarget,
  onDrop,
}) {
  return nodes.map(node => (
    <Node
      key={node.id}
      node={node}
      depth={depth}
      selectedNodeId={selectedNodeId}
      setSelectedNodeId={setSelectedNodeId}
      edit={edit}
      draggedNode={draggedNode}
      setDraggedNode={setDraggedNode}
      dropTarget={dropTarget}
      setDropTarget={setDropTarget}
      onDrop={onDrop}
    />
  ))
}

function Node({
  node,
  depth,
  selectedNodeId,
  setSelectedNodeId,
  edit,
  draggedNode,
  setDraggedNode,
  dropTarget,
  setDropTarget,
  onDrop,
}) {
  const inputRef = useRef()
  const [renaming, setRenaming] = useState(false)
  const [hover, setHover] = useState(false)
  const [id, setId] = useState(node.id)
  const selected = selectedNodeId === node.id
  const dragging = draggedNode?.id === node.id
  useEffect(() => {
    if (renaming) {
      setId(node.id)
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [renaming])
  const handleDragOver = (e, position) => {
    e.preventDefault()
    e.stopPropagation()
    // ignore self
    if (draggedNode.id === node.id) return
    // preventing adding as child of own child
    if (isNodeChildOf(node, draggedNode)) return
    // hitbox/collider must stay at root
    if ((draggedNode.type === 'hitbox' || draggedNode.type === 'collider') && (position === 'child' || depth > 0)) return // prettier-ignore
    // boxes can only be children of bones
    if (draggedNode.type === 'box' && position === 'child' && node.type !== 'bone') return
    // bones can only be children of other bones
    if (draggedNode.type === 'bone' && position === 'child' && node.type !== 'bone') return
    // all good!
    setDropTarget({ id: node.id, position })
  }
  const isDropBefore = dropTarget?.id === node.id && dropTarget?.position === 'before'
  const isDropAfter = dropTarget?.id === node.id && dropTarget?.position === 'after'
  const isDropChild = dropTarget?.id === node.id && dropTarget?.position === 'child'
  return (
    <div className='sasset-nodegroup'>
      <div
        className={cls('sasset-node-dropzone before', { highlight: isDropBefore })}
        onDragOver={e => handleDragOver(e, 'before')}
        css={css`
          left: 0px;
          left: ${depth * 20}px;
        `}
      />
      <div
        className={cls('sasset-node', { selected, dragging })}
        css={css`
          padding-left: ${depth * 20}px;
        `}
        onClick={() => setSelectedNodeId(node.id)}
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        draggable
        onDragStart={e => {
          e.stopPropagation()
          setDraggedNode(node)
        }}
        onDragEnd={e => {
          e.stopPropagation()
          onDrop()
        }}
        onDragOver={e => handleDragOver(e, 'child')}
      >
        <div className='sasset-node-inner'>
          <NodeIcon className='sasset-node-icon' type={node.type} size={18} />
          {!renaming && (
            <div className='sasset-node-name' onDoubleClick={() => setRenaming(true)}>
              {node.id}
            </div>
          )}
          {renaming && (
            <input
              className='sasset-node-name input'
              type='text'
              value={id}
              onChange={e => setId(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.target.blur()
                }
              }}
              onBlur={() => {
                setRenaming(false)
                edit(data => {
                  const match = findNode(data.asset.model.root.children, node.id)
                  match.id = getNewId(id, data.asset.model.root, node.id)
                  data.ui.nodeId = match.id
                  data.ui.version++
                })
              }}
              ref={inputRef}
            />
          )}
          {(hover || selected) && (
            <>
              <div
                className='sasset-node-btn'
                onClick={() => {
                  edit(data => {
                    const [match, parent, idx] = findNodeInfo(data.asset.model.root.children, node.id)
                    const newNode = cloneDeep(match)
                    // const newNode = cloneNode(match, store.asset.model.root)
                    if (parent) {
                      parent.children.splice(idx + 1, 0, newNode)
                    } else {
                      data.asset.model.root.children.splice(idx + 1, 0, newNode)
                    }
                    const clean = node => {
                      node.id = getNewId(node.id, data.asset.model.root)
                      for (const child of node.children) {
                        clean(child)
                      }
                    }
                    clean(newNode)
                    data.ui.version++
                  })
                }}
              >
                <CopyIcon size={14} />
              </div>
              <div
                className='sasset-node-btn'
                onClick={() => {
                  edit(data => {
                    const [match, parent, idx] = findNodeInfo(data.asset.model.root.children, node.id)
                    if (parent) {
                      parent.children.splice(idx, 1)
                    } else {
                      data.asset.model.root.children.splice(idx, 1)
                    }
                    data.ui.version++
                  })
                }}
              >
                <Trash2Icon size={14} />
              </div>
            </>
          )}
        </div>
        <div
          className={cls('sasset-node-dropzone child', { highlight: isDropChild })}
          css={css`
            left: 0;
            left: ${depth * 20 + 20}px;
          `}
        />
      </div>
      {node.children.length > 0 && (
        <Nodes
          depth={depth + 1}
          nodes={node.children}
          selectedNodeId={selectedNodeId}
          setSelectedNodeId={setSelectedNodeId}
          edit={edit}
          draggedNode={draggedNode}
          setDraggedNode={setDraggedNode}
          dropTarget={dropTarget}
          setDropTarget={setDropTarget}
          onDrop={onDrop}
        />
      )}
      <div
        className={cls('sasset-node-dropzone after', { highlight: isDropAfter })}
        onDragOver={e => handleDragOver(e, 'after')}
        css={css`
          left: 0;
          left: ${depth * 20}px;
        `}
      />
    </div>
  )
}

function NodeIcon({ type, ...props }) {
  if (type === 'bone') return <BoneIcon {...props} />
  if (type === 'box') return <BoxIcon {...props} />
  if (type === 'hitbox') return <ScanIcon {...props} />
  if (type === 'collider') return <BlendIcon {...props} />
}

function findNodeInfo(nodes, id, parent = null) {
  for (let idx = 0; idx < nodes.length; idx++) {
    const node = nodes[idx]
    if (node.id === id) return [node, parent, idx]
    if (node.children.length) {
      const [match, matchParent, idx] = findNodeInfo(node.children, id, node)
      if (match) return [match, matchParent, idx]
    }
  }
  return [null, null, null]
}

function findNode(nodes, id) {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i]
    if (node.id === id) return node
    if (node.children.length) {
      const match = findNode(node.children, id)
      if (match) return match
    }
  }
  return null
}

function isNodeChildOf(node, otherNode) {
  const match = findNode(otherNode.children, node.id)
  return !!match
}

// function useAssetTexture(width, height, pixels) {
//   const [version, setVersion] = useState(0)
//   const [canvas, ctx] = useMemo(() => {
//     const canvas = document.createElement('canvas')
//     canvas.width = width
//     canvas.height = height
//     const ctx = canvas.getContext('2d')
//     ctx.imageSmoothingEnabled = false
//     // console.log('useAssetTexture: canvas (re)built', width, height)
//     return [canvas, ctx]
//   }, [width, height])
//   useEffect(() => {
//     // console.log('useAssetTexture: canvas/pixels changed', canvas, pixels)
//     if (!pixels.length) return
//     // console.time('draw')
//     const arr = new Uint8ClampedArray(pixels)
//     const img = new ImageData(arr, width, height)
//     ctx.putImageData(img, 0, 0)
//     // console.timeEnd('draw')
//     setVersion(n => n + 1)
//   }, [canvas, pixels])
//   return { canvas, version }
// }

function canvasToFile(canvas) {
  return new Promise(resolve => {
    canvas.toBlob(async blob => {
      const hash = await hashFile(blob)
      const file = new File([blob], `${hash}.png`, { type: 'image/png' })
      resolve(file)
    })
  })
}

function ConditionField({ condition, onChange, onRemove }) {
  return (
    <div
      className='condition'
      css={css`
        display: flex;
        gap: 8px;
        margin: 0 0 8px;
        .condition-text {
          flex: 1;
        }
        .condition-op {
          width: 40px;
        }
        .condition-x {
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }
      `}
    >
      <div className='condition-text'>
        <UIEditorFieldText value={condition.prop} onChange={value => onChange('prop', value)} />
      </div>
      <div className='condition-op'>
        <UIEditorFieldBtn label={condition.op} onClick={() => onChange('op', nextOp(condition.op))} />
      </div>
      <div className='condition-text'>
        <UIEditorFieldText value={condition.value} onChange={value => onChange('value', value)} />
      </div>
      <div className='condition-x' onClick={onRemove}>
        <XIcon size={18} />
      </div>
    </div>
  )
}

const ops = ['eq', 'ne']
function nextOp(curr) {
  const idx = ops.indexOf(curr)
  return ops[idx + 1] || ops[0]
}

function canvasToUint8Array(canvas) {
  return new Promise(resolve => {
    canvas.toBlob(async blob => {
      const buffer = await blob.arrayBuffer()
      const data = new Uint8Array(buffer)
      resolve(data)
    })
  })
}
